import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, FileText, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setResults(res.data.results || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#050505] border-[#1F2937] p-0" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1F2937]">
          <Search size={18} className="text-[#9CA3AF] shrink-0" />
          <input
            data-testid="search-input"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search news, team, publications..."
            className="flex-1 bg-transparent text-[#F3F4F6] text-sm outline-none placeholder:text-[#4B5563]"
            autoFocus
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {loading && <p className="text-sm text-[#9CA3AF] text-center py-4">Searching...</p>}
          {!loading && query.length >= 2 && results.length === 0 && (
            <p className="text-sm text-[#4B5563] text-center py-4">No results found</p>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`search-result-${i}`}
              onClick={() => onOpenChange(false)}
              className="w-full flex items-start gap-3 p-3 rounded hover:bg-[#111111] text-left transition-colors"
            >
              {r.type === "news" ? <FileText size={16} className="text-[#EE1D25] mt-0.5 shrink-0" /> : <Users size={16} className="text-[#FFB000] mt-0.5 shrink-0" />}
              <div>
                <p className="text-sm text-[#F3F4F6] font-medium">{r.title || r.name}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">{r.summary || r.expertise || r.category}</p>
              </div>
            </button>
          ))}
          {!query && (
            <p className="text-xs text-[#4B5563] text-center py-6">Start typing to search across all content</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
